// Flat cartoon's draft as the form keeps it (spec G14-flat-cartoon, PR d): the server holds the
// member's draft (`.../templates/flat-cartoon/draft`), so it survives the panel; the form reads it,
// keeps its edits there, and asks the server's pack reader, director and stills. As Pixar Talking
// Ad's (`pixar-draft.ts`), with no shot director and no takes kept between runs.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod";

import { ApiError } from "../../../api/errors";
import {
  getGetFlatCartoonDraftQueryKey,
  useApproveFlatCartoonScript,
  useGetFlatCartoonDraft,
  useRegenerateFlatCartoonStill,
  useResetFlatCartoonDraft,
  useWriteFlatCartoonDraft,
  useWriteFlatCartoonScript,
  writeFlatCartoonDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  FlatCartoonDraft,
  FlatCartoonDraftStill,
  FlatCartoonScript,
  FlatCartoonStillName,
  FlatCartoonStills,
} from "../../../api/generated/model";
import {
  FlatCartoonApproval,
  FlatCartoonDraftWrite,
  FlatCartoonInputs,
  FlatCartoonScriptAsk,
  type FlatCartoonDraftWriteOutput,
  type FlatCartoonInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface FlatCartoonForm {
  inputs: FlatCartoonInputsOutput;
  script: FlatCartoonScript | null;
}

/** The stills in display order: the two character sheets, then A2 to F2. */
export const STILL_ORDER: readonly FlatCartoonStillName[] = [
  "host_sheet",
  "caller_sheet",
  "studio",
  "car",
  "metaphor",
  "prescribe",
  "closeup",
  "hero",
];

/** What each still is drawn from (`flat_cartoon_stills.REFERENCES`, the playbook's L3683 and
 * L3696-3700): the sheets from no still. */
export const STILL_SOURCES: Readonly<Partial<Record<FlatCartoonStillName, readonly FlatCartoonStillName[]>>> = {
  studio: ["host_sheet"],
  car: ["studio", "caller_sheet"],
  metaphor: ["studio"],
  prescribe: ["studio", "host_sheet"],
  closeup: ["prescribe", "host_sheet"],
  hero: ["studio"],
};

/** The six lines' speakers in the beats' order (the playbook's L3643). */
export const SPEAKERS = ["HOST", "CALLER", "NARRATOR", "HOST", "HOST", "NARRATOR"] as const;

/** One problem of the generated schema, in our words. */
function problemWords(issue: z.core.$ZodIssue): string {
  if (issue.code === "too_big" && issue.origin === "string") return `At most ${issue.maximum} characters.`;
  if (issue.code === "too_small" && issue.origin === "string") return "Fill this in.";
  return "This value is not valid.";
}

/**
 * The script's first problem on each of its fields (`lines` for any line), as Approve's body
 * checks it: an edit the schema refuses shows on its field, and Approve says why it waits.
 */
export function scriptProblems(form: FlatCartoonForm): Partial<Record<keyof FlatCartoonScript, string>> {
  if (form.script === null) return {};
  const body = FlatCartoonApproval.safeParse({ inputs: form.inputs, script: form.script });
  const problems: Partial<Record<keyof FlatCartoonScript, string>> = {};
  if (body.success) return problems;
  for (const issue of body.error.issues) {
    const [root, field] = issue.path;
    if (root !== "script" || typeof field !== "string" || !(field in form.script)) continue;
    problems[field as keyof FlatCartoonScript] ??= problemWords(issue);
  }
  return problems;
}

/** A script for the member to write: the six lines on their speakers, every text empty. */
export function blankScript(): FlatCartoonScript {
  return {
    lines: SPEAKERS.map((speaker) => ({ speaker, text: "" })),
    caller_name: "",
    host_lock: "",
    caller_lock: "",
    runner_lock: "",
    problem_metaphor: "",
    product_description: "",
    accent_colors: "",
    product_short: "",
    pack_word: "",
    car_props: "",
    tagline: "",
    pronunciations: [],
    host_recap: "",
    host_features: "",
    caller_recap: "",
    caller_features: "",
    runner_recap: "",
    runner_features: "",
    metaphor_action: "",
    benefit_number: "",
  };
}

/** The video's settings the footer keeps open once the script is approved: the form's own until
 * "Generate video" sends them. */
export interface VideoSettings {
  subtitles: boolean;
  resolution: FlatCartoonInputsOutput["resolution"];
}

/** The six start stills the clips start from, by name, once every still is done; null before. */
export function drawnStills(draft: FlatCartoonDraft): FlatCartoonStills | null {
  if (draft.stage !== "cast" || draft.stills.length === 0) return null;
  const drawn = Object.fromEntries(
    draft.stills.flatMap((each) => (each.output ? [[each.name, each.output] as const] : [])),
  );
  if (draft.stills.some((each) => !(each.name in drawn))) return null;
  const { studio, car, metaphor, prescribe, closeup, hero } = drawn;
  if (!studio || !car || !metaphor || !prescribe || !closeup || !hero) return null;
  return { studio, car, metaphor, prescribe, closeup, hero };
}

/** "Generate video"'s body: the approved script, the brand as said and the web address, the start
 * stills by reference and the footer's settings; null until every still is done. */
export function videoBody(draft: FlatCartoonDraft, settings: VideoSettings) {
  if (draft.script === null) return null;
  const stills = drawnStills(draft);
  const inputs = FlatCartoonInputs.safeParse(draft.inputs);
  if (stills === null || !inputs.success) return null;
  return {
    type: "video.template" as const,
    inputs: {
      template: "flat-cartoon" as const,
      script: draft.script,
      brand: inputs.data.brand.trim(),
      url: inputs.data.url.trim(),
      stills,
      ...settings,
    },
  };
}

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** A still under way: drawing, or pending on stills each done or still coming. */
export function stillWaiting(name: FlatCartoonStillName, stills: readonly FlatCartoonDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const sources = STILL_SOURCES[name] ?? [];
  return (
    sources.length > 0 &&
    sources.every(
      (source) => stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills),
    )
  );
}

/** Where the draft's work stands, as the progress line names it. */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  drawing: boolean;
}

export function workOf(draft: FlatCartoonDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: FlatCartoonDraft): FlatCartoonForm {
  const inputs = FlatCartoonInputs.safeParse(draft.inputs);
  return { inputs: inputs.success ? inputs.data : FlatCartoonInputs.parse({}), script: draft.script };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useFlatCartoonDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetFlatCartoonDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<FlatCartoonForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetFlatCartoonDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: FlatCartoonDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteFlatCartoonScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveFlatCartoonScript(asks);
  const regeneration = useRegenerateFlatCartoonStill(asks);
  const reset = useResetFlatCartoonDraft(asks);
  const asking = script.isPending || approval.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined ? { reading: false, writing: false, drawing: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteFlatCartoonDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: FlatCartoonDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) writeFlatCartoonDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = FlatCartoonDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<FlatCartoonForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<FlatCartoonInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = FlatCartoonScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = FlatCartoonApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: FlatCartoonStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
